import React, { Fragment } from 'react';
import moment from 'moment';
import { When } from 'react-if';
import { useTranslation } from 'react-i18next';
import { Spacing, Text, Icon, MessageBox } from '@reservamos/elements';
import SendEmail from 'components/purchase/SendEmail';
import PaymentStatusIcon from 'components/purchase/PaymentStatusIcon';
import PaymentStepStatus from 'components/purchase/PaymentStepStatus';
import TicketSummary from 'ui/molecules/TicketSummary';
import PurchaseToken from 'ui/atoms/PurchaseToken';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import usePurchase from 'hooks/store/usePurchase';
import {
  purchaseHasFreeFarePassenger,
  purchaseIsAllFreeFareTickets,
} from 'utils/freeFareDocumentRules';
import usePayment from 'hooks/store/usePayment';
import useSeatsBus from 'hooks/store/useSeatsBus';
import { generateGoogleMapsSearchUrl } from 'utils/urls';
import parsePurchaseStatus from 'utils/parsePurchaseStatus';
import emojiParty from 'images/icons/emoji-party.png';
import 'styles/components/purchase/PurchasesLayout';
import wayIsOpenTicket from 'utils/wayIsOpenTicket';
import PassengerTicketsList from 'ui/molecules/PassengerTicketsList';
import LapChildDownloadButton from 'ui/atoms/LapChildDownloadButton';
import { getSeatPriceZonesForPassengersLeg } from 'utils/seatPricing';
import normalizeSearchWidgetDateFormat from 'utils/normalizeSearchWidgetDateFormat';
import PurchaseCompleteBanners from '../PurchaseCompleteBanners';

const getTicketSummaryParams = (tripFragment, hideViewLocation) => {
  const originMapsUrl = hideViewLocation
    ? null
    : generateGoogleMapsSearchUrl({
        lat: tripFragment.origin.coordinates.lat,
        long: tripFragment.origin.coordinates.long,
      });
  const destinationMapsUrl = hideViewLocation
    ? null
    : generateGoogleMapsSearchUrl({
        lat: tripFragment.destination.coordinates.lat,
        long: tripFragment.destination.coordinates.long,
      });
  return {
    originTime: moment(tripFragment.departure).format('LT'),
    originTerminal: tripFragment.origin.name,
    originDirections: tripFragment.origin.directions,
    originCity: tripFragment.origin.cityName,
    originLocation: originMapsUrl,
    destinationTime: moment(tripFragment.arrival).format('LT'),
    destinationTerminal: tripFragment.destination.name,
    destinationDirections: tripFragment.destination.directions,
    destinationCity: tripFragment.destination.cityName,
    destinationLocation: destinationMapsUrl,
    href: tripFragment.ticketPdfUrl,
  };
};

const DownloadTicketsSection = () => {
  const { t } = useTranslation('purchase');
  const { searchWidget } = useWhitelabelEnvs();
  const purchase = usePurchase();
  const { token, state: purchaseStatus, email, departs, returns, passengers } = purchase;
  const hidePurchaseOrderReferenceUi = purchaseIsAllFreeFareTickets(purchase);
  const seatsBus = useSeatsBus();
  const { paymentType } = usePayment();
  const status = parsePurchaseStatus(purchaseStatus, paymentType);
  const { brand } = useWhitelabelEnvs();
  const features = useWhitelabelFeatures();
  const showFreeFareAttention =
    features.FREE_FARE_ATTENTION && purchaseHasFreeFarePassenger(purchase);
  const displayPurchaseToken =
    features.TICKETS_REFERENCE === 'purchase-token' && !features.SHOW_RESERVE_CODE;
  const tenantUsesRemoteTickets = features.REMOTE_TICKETS;
  const displayDownloadTicketsButton = !tenantUsesRemoteTickets;
  const departureFragments = departs.fragments || [];
  const returnFragments = returns.fragments || [];
  const departureIsOpenTicket = wayIsOpenTicket(departs);
  const returnIsOpenTicket = wayIsOpenTicket(returns);
  const departureDate = moment(departs.departure).format(
    normalizeSearchWidgetDateFormat(searchWidget?.dateFormat, 'DD MMM'),
  );
  const returnDate = returns.departure
    ? moment(returns.departure).format(
        normalizeSearchWidgetDateFormat(searchWidget?.dateFormat, 'DD MMM'),
      )
    : '';
  const hideViewLocation = features.HIDE_LOCATION_ON_PURCHASE_COMPLETE;
  const purchaseType = returnDate ? 'round_trip' : 'one_way';
  const isOpenTicket = Boolean(departureIsOpenTicket || returnIsOpenTicket);

  const renderTicketsSummaryTitle = ({ title, date, isOpenTicket }) => {
    return (
      <Text size="L" weight="bold">
        {isOpenTicket ? title : `${title} - ${date}`}
      </Text>
    );
  };

  const renderTicketsSummary = (fragments, waySelectedSeats) => {
    const waySeats = Array.isArray(waySelectedSeats) ? waySelectedSeats : [];

    return fragments.map((fragment) => {
      const fragmentTickets = fragment.tickets || [];
      const fragmentHasTickets = fragmentTickets.length > 0;
      const fragmentSeatPriceZones = getSeatPriceZonesForPassengersLeg(fragment, seatsBus);
      const fromFragment = Array.isArray(fragment?.selectedSeats) ? fragment.selectedSeats : [];
      const selectedSeatsForPassengers = fromFragment.length > 0 ? fromFragment : waySeats;

      if (!fragmentHasTickets) {
        return null;
      }

      const infantTickets = Array.isArray(fragment.infantTickets) ? fragment.infantTickets : [];
      const ticketSummaryParams = getTicketSummaryParams(fragment, hideViewLocation);
      return (
        <Fragment key={ticketSummaryParams.href}>
          <TicketSummary
            {...ticketSummaryParams}
            isOpen={fragment.openTicket}
            buttonLabel={t(!fragment.openTicket ? 'get_ticket' : 'get_ticket_open')}
          />
          <When condition={displayPurchaseToken && !hidePurchaseOrderReferenceUi}>
            <Text size="S">
              {`${t('purchase_reference')} `}
              <Text size="S" elementType="span" weight="bold">
                {fragment.transporterKey}
              </Text>
            </Text>
          </When>
          {infantTickets
            .filter((infantTicket) => infantTicket?.documentUrl)
            .map((infantTicket) => (
              <LapChildDownloadButton key={infantTicket.id} href={infantTicket.documentUrl} />
            ))}
          <PassengerTicketsList
            tickets={fragmentTickets}
            passengers={passengers}
            purchaseToken={token}
            areOpenTickets={fragment.openTicket}
            seatPriceZones={fragmentSeatPriceZones}
            selectedSeats={selectedSeatsForPassengers}
          />
        </Fragment>
      );
    });
  };

  return (
    <Spacing vertical size="XXL">
      <div className="purchase-layout-content-wrapper">
        <Spacing vertical>
          <Spacing vertical size="XS">
            <Text weight="bold" size="XL">
              {t('label.tickets_ready')}
              <Icon type={emojiParty} hasMargin marginSide="left" />
            </Text>
            <Text color="grayMedium">{t('ticket_instructions_digital', { context: brand })}</Text>
          </Spacing>
          {token && displayPurchaseToken && !hidePurchaseOrderReferenceUi && (
            <PurchaseToken token={token} />
          )}
        </Spacing>
        <PaymentStatusIcon />
      </div>

      <When condition={displayDownloadTicketsButton}>
        <Spacing vertical>
          {renderTicketsSummaryTitle({
            title: t(!departureIsOpenTicket ? 'ticket_departure' : 'ticket_departure_open'),
            date: departureDate,
            isOpenTicket: departureIsOpenTicket,
          })}
          {renderTicketsSummary(departureFragments, departs?.selectedSeats)}
          <When condition={returnDate}>
            {renderTicketsSummaryTitle({
              title: t(!returnIsOpenTicket ? 'ticket_return' : 'ticket_return_open'),
              date: returnDate,
              isOpenTicket: returnIsOpenTicket,
            })}
          </When>
          {renderTicketsSummary(returnFragments, returns?.selectedSeats)}
        </Spacing>
      </When>
      {showFreeFareAttention && (
        <MessageBox
          borderColor="warning"
          borderRadius="L"
          iconType="Warning"
          iconSize="M"
          titleIcon
          title={t('purchase:free_fare_attention.title')}
          widthFull
        >
          <Text size="S">{t('purchase:free_fare_attention.body')}</Text>
        </MessageBox>
      )}
      <Spacing flexGrow isResponsive>
        <Spacing vertical size="S">
          <PaymentStepStatus
            transporterType="bus"
            status={status.sent}
            title="sent"
            email={email}
          />
          <Text size="S">{t('payment:text.check_spam_email')}</Text>
        </Spacing>
        <SendEmail />
      </Spacing>
      <PurchaseCompleteBanners purchaseType={purchaseType} isOpenTicket={isOpenTicket} />
    </Spacing>
  );
};

export default DownloadTicketsSection;
